import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useForm } from 'react-hook-form';
import { USER } from "../../services/actions";
import {
    Box,
    Typography,
    Avatar,
    Button,
    Container,
    Paper,
    Divider,
    IconButton,
    Dialog,
    DialogContent
} from "@mui/material";
import { IoEye, IoEyeOff } from "react-icons/io5";
import { FiUpload, FiCalendar } from "react-icons/fi";
import ImageResizer from '../extra/ImageResizer';

// --- EXACT STYLES ---
const inputBaseStyle = {
    width: '100%',
    height: '45px',
    padding: '0 12px',
    borderRadius: '6px',
    border: '1px solid #D1D5DB',
    outline: 'none',
    fontSize: '14px',
    color: '#111827',
    fontFamily: '"Poppins", sans-serif',
    transition: 'border-color 0.2s',
    backgroundColor: '#fff',
    '&:focus': { borderColor: '#F59E0B' },
    '&::placeholder': { color: '#9CA3AF' },
    '&:disabled': { backgroundColor: '#F9FAFB', color: '#6B7280' }
};

const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    color: '#374151',
    fontWeight: 500,
    fontFamily: '"Poppins", sans-serif',
};

// Row container to hold two inputs side-by-side
const FormRow = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3, width: '100%' }}>
        {children}
    </Box>
);

// Individual Input Column (50% width)
const FormCol = ({ children }) => (
    <Box sx={{ flex: 1, width: '100%' }}>
        {children}
    </Box>
);

const DetailRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}>
        <Typography sx={{ width: '150px', flexShrink: 0, color: '#6B7280', fontSize: '15px', fontWeight: 400 }}>{label}</Typography>
        <Typography sx={{ mr: 3, color: '#6B7280' }}>:</Typography>
        <Typography sx={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{value || ''}</Typography>
    </Box>
);

export default function Profile() {
    const dispatch = useDispatch();
    const user = useSelector((state) => state?.loginState.auth);
    
    const [isEditing, setIsEditing] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [pendingData, setPendingData] = useState(null);

    const { register, handleSubmit, setValue, reset } = useForm({ defaultValues: user, mode: "all" });

    useEffect(() => { if (user) reset(user); }, [user, reset]);

    const onFormSubmit = (data) => {
        setPendingData(data);
        setShowConfirm(true);
    };

    const handleConfirmSave = () => {
        if (!pendingData) return;
        let data = { ...pendingData };
        if (!data['avatar'] || String(data['avatar']).startsWith("http")) delete data['avatar'];
        let payload = {};
        ["id", "avatar", "first_name", "last_name", "email", "phone", "username", "password"].forEach((key) => {
            if (data[key]) payload[key] = data[key];
        });
        dispatch({
            type: `${USER}_PATCH`, 
            payload: payload, 
            callback: (resp) => {
                dispatch({ type: "LOGIN_AUTH", payload: resp, local: true });
                setShowConfirm(false);
                setIsEditing(false);
            }
        });
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3, fontFamily: '"Poppins", sans-serif' }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '14px', color: '#9CA3AF', fontWeight: 500 }}>Dashboard</Typography>
                <Typography sx={{ mx: 1, fontSize: '14px', color: '#9CA3AF' }}>{'>'}</Typography>
                <Typography sx={{ fontSize: '14px', color: isEditing ? '#9CA3AF' : '#111827', fontWeight: isEditing ? 500 : 600 }}>Profile</Typography>
                {isEditing && (
                    <>
                        <Typography sx={{ mx: 1, fontSize: '14px', color: '#9CA3AF' }}>{'>'}</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#111827', fontWeight: 600 }}>Edit Profile</Typography>
                    </>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: '16px', bgcolor: 'white', minHeight: '80vh', width: '100%' }}>
                {!isEditing ? (
                    // VIEW PROFILE
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                        <Avatar src={user?.avatar} sx={{ width: 110, height: 110, mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 0.5, fontSize: '22px' }}>{user?.first_name} {user?.last_name}</Typography>
                        <Typography sx={{ color: '#6B7280', mb: 0.5, fontSize: '15px' }}>{user?.main_role || 'Work Supervisor'}</Typography>
                        <Typography sx={{ color: '#9CA3AF', fontSize: '13px', mb: 4 }}>{user?.company_name || 'Al Jaber Engineering'}</Typography>
                        <Divider sx={{ width: '100%', maxWidth: '600px', mb: 5, borderColor: '#E5E7EB' }} />
                        <Box sx={{ width: '100%', maxWidth: '550px' }}>
                            <DetailRow label="First name" value={user?.first_name} />
                            <DetailRow label="Last name" value={user?.last_name} />
                            <DetailRow label="Email" value={user?.email} />
                            <DetailRow label="Phone" value={user?.phone} />
                            <DetailRow label="Phone" value={user?.phone} />
                            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #F3F4F6' }}>
                                <DetailRow label="User name" value={user?.username || user?.email} />
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ width: '150px', flexShrink: 0, color: '#6B7280', fontSize: '15px' }}>Password</Typography>
                                    <Typography sx={{ mr: 3, color: '#6B7280' }}>:</Typography>
                                    <Typography sx={{ color: '#111827', fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>************ <IoEye color="#9CA3AF" /></Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ mt: 5, display: 'flex', gap: 2 }}>
                            <Button variant="outlined" sx={{ textTransform: 'none', borderColor: '#E5E7EB', color: '#111827', borderRadius: '8px', px: 4, height: '40px', fontWeight: 500 }} Back></Button>
                            <Button variant="contained" onClick={() => setIsEditing(true)} sx={{ textTransform: 'none', bgcolor: '#FCD34D', color: '#111827', borderRadius: '8px', px: 4, height: '40px', fontWeight: 600, boxShadow: 'none' }}>Edit</Button>
                        </Box>
                    </Box>
                ) : (
                    // EDIT PROFILE - FIXED LAYOUT USING FLEXBOX
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ width: '100%', maxWidth: '850px' }}>
                            <form onSubmit={handleSubmit(onFormSubmit)}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 4, fontSize: '18px', color: '#111827' }}>Update your profile details</Typography>

                                <FormRow>
                                    <FormCol>
                                        <label style={labelStyle}>First name</label>
                                        <Box component="input" {...register("first_name")} sx={inputBaseStyle} />
                                    </FormCol>
                                    <FormCol>
                                        <label style={labelStyle}>Last name</label>
                                        <Box component="input" {...register("last_name")} sx={inputBaseStyle} />
                                    </FormCol>
                                </FormRow>

                                <FormRow>
                                    <FormCol>
                                        <label style={labelStyle}>Email</label>
                                        <Box component="input" {...register("email")} sx={inputBaseStyle} />
                                    </FormCol>
                                    <FormCol>
                                        <label style={labelStyle}>Mobile no.</label>
                                        <Box component="input" {...register("phone")} sx={inputBaseStyle} />
                                    </FormCol>
                                </FormRow>

                                <FormRow>
                                    <FormCol>
                                        <label style={labelStyle}>Role</label>
                                        <Box component="select" disabled sx={{ ...inputBaseStyle, appearance: 'none', bg: '#F9FAFB' }}>
                                            <option>{user?.main_role || 'Work Supervisor'}</option>
                                        </Box>
                                    </FormCol>
                                    <FormCol>
                                        <label style={labelStyle}>Joining date</label>
                                        <Box sx={{ position: 'relative' }}>
                                            <Box component="input" readOnly defaultValue="20 January 2024" sx={{ ...inputBaseStyle, paddingRight: '40px' }} />
                                            <Box sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#6B7280' }}><FiCalendar size={18} /></Box>
                                        </Box>
                                    </FormCol>
                                </FormRow>

                                <FormRow>
                                    <FormCol>
                                        <label style={labelStyle}>Profile picture</label>
                                        <Box sx={{ position: 'relative' }}>
                                            <Box sx={{ ...inputBaseStyle, display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#374151' }}>Click here to browse<Box sx={{ ml: 'auto' }}><FiUpload size={18} /></Box></Box>
                                            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0 }}>
                                                <ImageResizer onChange={(bs64) => setValue("avatar", bs64)} />
                                            </Box>
                                        </Box>
                                        <Typography sx={{ fontSize: '11px', color: '#D1D5DB', mt: 0.5 }}>File size should not exceed 5MB</Typography>
                                    </FormCol>
                                    <FormCol>
                                        <label style={labelStyle}>&nbsp;</label>
                                        <Box component="input" placeholder="Ex:Image_file.jpeg" disabled sx={{ ...inputBaseStyle, bgcolor: '#F9FAFB' }} />
                                    </FormCol>
                                </FormRow>

                                {/* Divider exactly matching the width of the form */}
                                <Divider sx={{ my: 4, borderColor: '#F3F4F6' }} />

                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '18px', color: '#111827' }}>Change password</Typography>

                                <FormRow>
                                    <FormCol>
                                        <label style={labelStyle}>User name</label>
                                        <Box component="input" {...register("username")} sx={{ ...inputBaseStyle, bgcolor: '#F9FAFB', color: '#6B7280' }} readOnly />
                                    </FormCol>
                                    <FormCol>
                                        <label style={labelStyle}>Password</label>
                                        <Box sx={{ position: 'relative' }}>
                                            <Box component="input" type={showPassword ? "text" : "password"} value="************" readOnly sx={inputBaseStyle} />
                                            <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ position: 'absolute', right: 5, top: 2, color: '#374151' }}>
                                                {showPassword ? <IoEyeOff size={18} /> : <IoEye size={18} />}
                                            </IconButton>
                                        </Box>
                                    </FormCol>
                                </FormRow>

                                <FormRow>
                                    <FormCol>
                                        <label style={labelStyle}>New password</label>
                                        <Box component="input" type="password" placeholder="Enter new password" {...register("password")} sx={{ ...inputBaseStyle, bgcolor: '#F3F4F6' }} />
                                    </FormCol>
                                    <FormCol>
                                        <label style={labelStyle}>Re enter new password</label>
                                        <Box component="input" type="password" placeholder="Re enter new password" sx={{ ...inputBaseStyle, bgcolor: '#F3F4F6' }} />
                                    </FormCol>
                                </FormRow>

                                <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 2 }}>
                                    <Button onClick={() => { setIsEditing(false); reset(user); }} variant="outlined" sx={{ textTransform: 'none', borderColor: '#E5E7EB', color: '#374151', borderRadius: '8px', width: '100px', height: '40px', fontWeight: 500 }}>Cancel</Button>
                                    <Button type="submit" variant="contained" sx={{ textTransform: 'none', bgcolor: '#FCD34D', color: '#111827', borderRadius: '8px', width: '100px', height: '40px', fontWeight: 600, boxShadow: 'none' }}>Save</Button>
                                </Box>
                            </form>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* CONFIRMATION DIALOG - TEXT ABOVE ICON */}
            <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1, width: '530px', maxWidth: '95%' }}}>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2, px: 4 }}>
                    
                    {/* 1. TEXT IS NOW AT THE TOP */}
                    <Typography sx={{ fontWeight: 600, fontSize: '20px', mb: 4, color: '#111827' }}>
                        Are you sure you want to save the changes?
                    </Typography>
                    
                    {/* 2. ICON IS NOW IN THE MIDDLE WITH GAP */}
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 5 }}>
                         <img 
                            src="/streamline-color_user-profile-focus-flat.svg" 
                            alt="Confirm" 
                            style={{ width: '120px', height: '120px' }} 
                        />
                    </Box>
                    
                    {/* 3. BUTTONS AT THE BOTTOM */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button onClick={() => setShowConfirm(false)} variant="outlined" sx={{ textTransform: 'none', borderColor: '#E5E7EB', color: '#374151', borderRadius: '8px', px: 4, height: '44px', width: '110px', fontWeight: 500 }}>Cancel</Button>
                        <Button onClick={handleConfirmSave} variant="contained" sx={{ textTransform: 'none', bgcolor: '#FCD34D', color: '#111827', borderRadius: '8px', px: 4, height: '44px', width: '110px', boxShadow: 'none', fontWeight: 600 }}Save></Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Container>
    );
}